// Défilement automatique de la pellicule (mise en page horizontale, voir main.css) : quand elle compte plus de vues qu'il n'en
// tient dans le bandeau, elle va et vient tout doucement d'un bout à l'autre, avec une pause à chaque extrémité. Le joueur reste
// maître : dès qu'il la touche, la fait défiler ou utilise la molette, le défilement s'arrête et ne reprend qu'après un moment
// sans geste. Sans effet quand tout tient (portrait, peu de vues) ni si l'utilisateur préfère moins de mouvement.

export const FILM_SCROLL_SPEED = 14;   // pixels par seconde : lent, pour rester reposant
export const FILM_SCROLL_HOLD_MS = 1800; // pause à chaque extrémité
export const FILM_SCROLL_RESUME_MS = 3500; // temps sans geste avant de reprendre

/**
 * @param {HTMLElement} strip  l'élément qui défile horizontalement
 * @param {{ speed?: number, holdMs?: number, resumeMs?: number, win?: Window }} [options]
 */
export function createFilmAutoScroll(strip, { speed = FILM_SCROLL_SPEED, holdMs = FILM_SCROLL_HOLD_MS, resumeMs = FILM_SCROLL_RESUME_MS, win = window } = {}) {
  let pos = 0;        // position décimale (scrollLeft peut être arrondi par le navigateur)
  let lastSet = 0;    // dernière valeur écrite dans scrollLeft : un écart révèle un geste du joueur
  let direction = 1;
  let hold = 0;       // ms de pause restantes
  let idle = 0;       // ms avant de reprendre après un geste
  let frame = null;
  let last = null;

  const reducedMotion = () => !!win.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const touched = () => { idle = resumeMs; };

  /** Avance de `dtMs` millisecondes. Retourne vrai si la pellicule a bougé. */
  function tick(dtMs) {
    const max = strip.scrollWidth - strip.clientWidth;
    if (max <= 1 || reducedMotion()) { pos = 0; return false; }
    if (Math.abs(strip.scrollLeft - lastSet) > 1.5) { // le joueur a fait défiler la pellicule
      pos = strip.scrollLeft;
      lastSet = pos;
      touched();
    }
    if (idle > 0) { idle -= dtMs; return false; }
    if (hold > 0) { hold -= dtMs; return false; }
    pos += direction * speed * (dtMs / 1000);
    if (pos >= max) { pos = max; direction = -1; hold = holdMs; }
    else if (pos <= 0) { pos = 0; direction = 1; hold = holdMs; }
    strip.scrollLeft = pos;
    lastSet = strip.scrollLeft;
    return true;
  }

  function loop(now) {
    if (last !== null) tick(Math.min(100, now - last)); // un onglet resté caché ne fait pas sauter la pellicule
    last = now;
    frame = win.requestAnimationFrame(loop);
  }

  for (const type of ['pointerdown', 'touchstart', 'wheel']) strip.addEventListener(type, touched, { passive: true });
  frame = win.requestAnimationFrame?.(loop) ?? null;

  return {
    tick,
    destroy() {
      if (frame !== null) win.cancelAnimationFrame?.(frame);
      for (const type of ['pointerdown', 'touchstart', 'wheel']) strip.removeEventListener(type, touched);
    },
  };
}
